// Pure locale constants shared by the frontend i18n setup AND the Convex
// backend (dynamic-rendering endpoint). This module MUST stay free of any
// browser- or bundler-only APIs (no `import.meta`, no localStorage, no React)
// so it can be imported from the Convex runtime, which cannot evaluate
// `import.meta.glob`. `src/i18n.ts` re-exports everything here for backwards
// compatibility, so existing imports keep working unchanged.

// Locale metadata with display information.
// Italian is the primary language for Enkey Agency. Additional non-European
// locales (zh, ar, ru) can be appended here later without other code changes.
export const SUPPORTED_LOCALES = {
  it: {
    code: "it",
    emoji: "🇮🇹",
    name: "Italian",
    nativeName: "Italiano",
    dir: "ltr",
  },
  en: {
    code: "en",
    emoji: "🇬🇧",
    name: "English",
    nativeName: "English",
    dir: "ltr",
  },
  es: {
    code: "es",
    emoji: "🇪🇸",
    name: "Spanish",
    nativeName: "Español",
    dir: "ltr",
  },
  fr: {
    code: "fr",
    emoji: "🇫🇷",
    name: "French",
    nativeName: "Français",
    dir: "ltr",
  },
  de: {
    code: "de",
    emoji: "🇩🇪",
    name: "German",
    nativeName: "Deutsch",
    dir: "ltr",
  },
} as const;

// Default locale (must be a key in SUPPORTED_LOCALES)
export const DEFAULT_LOCALE: keyof typeof SUPPORTED_LOCALES = "it";

// Derive constants from metadata (single source of truth)
export const SUPPORTED_LOCALES_ARRAY = Object.keys(SUPPORTED_LOCALES) as Array<
  keyof typeof SUPPORTED_LOCALES
>;
export type SupportedLocale = keyof typeof SUPPORTED_LOCALES;

// Check if a locale code is supported
export function isSupportedLocale(
  locale: string | undefined,
): locale is SupportedLocale {
  return (
    !!locale && SUPPORTED_LOCALES_ARRAY.includes(locale as SupportedLocale)
  );
}

// Resolve browser preferences such as "en-US" or "de-DE" to one of the
// localized public editions. The ordered list supplied by the browser is
// respected and unsupported languages are skipped.
export function preferredSupportedLocale(
  languages: readonly string[] | undefined,
): SupportedLocale | null {
  for (const language of languages ?? []) {
    const primary = language.trim().toLowerCase().split(/[-_]/)[0];
    if (isSupportedLocale(primary)) return primary;
  }
  return null;
}
